import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from 'react-oidc-context';
import { COGNITO } from '@shared/lib/constants';
import { setSigningOut } from '@/lib/authToken';
import { cn } from '@/lib/utils';
import { isKiosk } from '@shared/lib/constants';
import { ServicePasswordModal } from '@/components/nav/ServicePasswordModal';

export function UserMenu() {
  const auth = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, right: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Service-menu long-press: 5s pointer-down on this pill opens the
  // password modal. Only active in kiosk mode (cloud users use Cognito).
  const [serviceModalOpen, setServiceModalOpen] = useState(false);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressFired = useRef(false);
  const startLongPress = () => {
    if (!isKiosk) return;
    longPressFired.current = false;
    longPressTimer.current = setTimeout(() => {
      longPressFired.current = true;
      setServiceModalOpen(true);
    }, 5000);
  };
  const cancelLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const user = auth.user?.profile;
  const displayName = user?.name || (user?.email as string | undefined)?.split('@')[0] || 'User';
  const initial = displayName.charAt(0).toUpperCase();
  const email = (user?.email as string | undefined) || '';

  const handleSignOut = useCallback(() => {
    setSigningOut(true);
    auth.removeUser();
    const params = new URLSearchParams({
      client_id: COGNITO.userClientId,
      logout_uri: window.location.origin,
    });
    window.location.href = `${COGNITO.hostedUiDomain}/logout?${params}`;
  }, [auth]);

  useEffect(() => {
    if (!isOpen) return;
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      setDropdownPos({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (!triggerRef.current?.contains(e.target as Node) && !(e.target as Element).closest('[data-user-menu-dropdown]')) {
        setIsOpen(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => { if (e.key === 'Escape') setIsOpen(false); };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  return (
    <>
      <button
        ref={triggerRef}
        onClick={() => {
          if (longPressFired.current) {
            longPressFired.current = false;
            return;
          }
          if (isKiosk) return;
          setIsOpen((o) => !o);
        }}
        onPointerDown={startLongPress}
        onPointerUp={cancelLongPress}
        onPointerLeave={cancelLongPress}
        onPointerCancel={cancelLongPress}
        title={email || displayName}
        className={cn(
          'flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors select-none',
          isOpen ? 'bg-accent/10 text-accent' : 'text-muted hover:text-foreground'
        )}
        style={{ WebkitTouchCallout: 'none', WebkitUserSelect: 'none', userSelect: 'none' }}
      >
        <span className="flex items-center justify-center w-5 h-5 rounded-full bg-accent/20 text-accent text-[10px] font-semibold">
          {initial}
        </span>
        {displayName.split(' ')[0]}
      </button>

      {isOpen && createPortal(
        <div
          data-user-menu-dropdown
          className="fixed z-[100] min-w-48 rounded-xl border border-border bg-surface/95 backdrop-blur-sm shadow-lg py-1"
          style={{ top: dropdownPos.top, right: dropdownPos.right }}
        >
          {email && (
            <div className="px-3 py-2 text-xs text-muted border-b border-border truncate">
              {email}
            </div>
          )}
          <button
            onClick={handleSignOut}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-foreground hover:bg-surface-raised transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            Sign Out
          </button>
        </div>,
        document.body
      )}

      <ServicePasswordModal open={serviceModalOpen} onClose={() => setServiceModalOpen(false)} />
    </>
  );
}
